import { useEffect, useMemo, useState } from 'react'
import { keyBy } from 'es-toolkit'
import { OperatorOrganization } from '@/features/auth/api/AuthModel'
import { FinalizedReportPreview } from '@/hooks/api/finalizedReports'
import useI18n from '@/shared/hooks/useI18n'
import FinalizedReportPage from './FinalizedReportPage'
import FinalizedReportCoverPage from './FinalizedReportCoverPage'
import FinalizedReportDeclarantsBanner from './FinalizedReportDeclarantsBanner'
import { ArtemSpinner, productionMethods } from '@/common'
import FinalizedReportDetailsTable from './FinalizedReportDetailsTable'
import { SectionTitle, SubSectionTitle } from './typography'
import FinalizedReportEmissionQualifyingParams from './FinalizedReportEmissionQualifyingParams'
import FinalizedReportElectricityEmissionsTable from './FinalizedReportElectricityEmissionsTable'
import FinalizedReportSpecificEmbeddedEmissionsTable from './FinalizedReportSpecificEmbeddedEmissionsTable'
import FinalizedReportCombinedElectricityEmissionsTable from './FinalizedReportCombinedElectricityEmissionsTable'
import { dataQualityOptions, justificationOptions, qualityAssuranceOptions } from '../constants'
import { useTranslation } from 'react-i18next'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'

export type EntityConfig = {
  entityName: string
  street: string
  city: string
  country: string
  email: string
  phone: string
  website: string
}

function repeat(char: string, times: number) {
  return Array.from({ length: times }, () => char).join('')
}

function getUnlocodeWithCountry(unlocode: string, country: string) {
  return unlocode ? `${country.toUpperCase()} ${unlocode}` : null
}

const FINALIZED_REPORT_CONFIG: Record<string, EntityConfig> = {
  artem: {
    entityName: 'ARTEM Technologie AG',
    street: 'Sinserstraße 67',
    city: '6330 Cham Zug',
    country: 'Switzerland',
    email: 'info@artem.earth',
    phone: '+41 41 560 55 00',
    website: 'https://artem.earth'
  }
}

const displayDual = (
  doesRequireLocalAddress: boolean,
  en?: string | number | null,
  native?: string | number | null,
  opts?: { allowAlphaNumeric?: boolean }
) => {
  if (en === undefined || en === null || en === '') {return null}
  const isAlphanumeric = (value?: string | number | null) => {
    if (value === undefined || value === null) {return false}
    const stringValue = String(value)
    return /[A-Za-z\p{L}]/u.test(stringValue)
  }
  const showNative =
    native && doesRequireLocalAddress && (opts?.allowAlphaNumeric ? isAlphanumeric(native) : true)
  return (
    <span>
      {en}
      {showNative ? ' (' : ''}
      {showNative ? <em>{native as React.ReactNode}</em> : null}
      {showNative ? ')' : ''}
    </span>
  )
}

export default function FinalizedReportPreviewEmbedded({
  organization,
  data,
  isPreview,
  selectedPdfLanguage
}: {
  organization: OperatorOrganization
  data: FinalizedReportPreview
  isPreview?: boolean
  selectedPdfLanguage?: string
}) {
  const { t, dateLongFormatterGB } = useI18n()
  const { i18n } = useTranslation()
  const tLng = useMemo(() => ({ lng: selectedPdfLanguage }), [selectedPdfLanguage])
  const [isLangReady, setIsLangReady] = useState<boolean>(false)
  const {
    installation,
    productionRoute,
    startDate,
    endDate,
    methodAggregatedEmissions,
    emissionQualifyingParams,
    qualityAssuranceInfo,
    dataQualityInfo,
    aggregatedElectricity,
    finalizedAt,
    defaultValuesJustification: justificationInfo
  } = data

  const singleMethodAggregatedEmissions = methodAggregatedEmissions[0]

  const hasPrecursorEmissionFactors =
    singleMethodAggregatedEmissions.precursorSpecificIndirectEmissions > 0

  const operatorName = organization.name
  const productionRouteName = productionRoute.name
  const installationName = installation.name

  const doesRequireLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organization.placeOfRegistration
  )

  const firstProductionMethod = productionRoute.productionMethods[0]
  const productionMethod = t(`productionMethods.${firstProductionMethod?.code}`, tLng)
  const installationCountryName = t(`countriesByIsoAlpha2:${installation.country}`, tLng)
  const organizationCountryName = t(`countriesByIsoAlpha2:${organization.country}`, tLng)
  const unlocodeWithCountry = getUnlocodeWithCountry(installation.unlocode, installation.country)
  const productionMethodCodes = productionRoute.productionMethods.map(method => method.code)

  const isApplicableForActivityLevelValidation = useMemo(() => {
    return productionMethodCodes.some(
      code =>
        productionMethods.find(method => method.code === code)
          ?.isApplicableForActivityLevelValidation
    )
  }, [productionMethodCodes])

  const sourcesByType = keyBy(aggregatedElectricity.sources, source => source.type)
  const isUsingGrid = !!sourcesByType['GRID']
  const isUsingDirectTechnicalLink = !!sourcesByType['DIRECT_TECHNICAL_LINK']
  const isUsingPPA = !!sourcesByType['PPA']

  const startDateFormatted = dateLongFormatterGB(new Date(startDate))
  const endDateFormatted = dateLongFormatterGB(new Date(endDate))
  const creationDateFormatted = dateLongFormatterGB(new Date(data.createdAt))
  const finalizedDateFormatted = useMemo(() => {
    if (!finalizedAt) {
      return t('finalizedReportDocument:common.tba', tLng)
    }

    const date = new Date(finalizedAt)

    return dateLongFormatterGB(date)
  }, [finalizedAt, dateLongFormatterGB, t, selectedPdfLanguage])

  const electricityCombined = aggregatedElectricity.sources.reduce(
    (acc, source) => {
      return {
        totalConsumptionShare: acc.totalConsumptionShare + source.totalConsumptionShare,
        weightedAvgEmissionFactor:
          acc.weightedAvgEmissionFactor +
          source.weightedAvgEmissionFactor * source.totalConsumptionShare
      }
    },
    { totalConsumptionShare: 0, weightedAvgEmissionFactor: 0 }
  )

  const cnCodes = productionRoute.productionMethods.flatMap(method =>
    method.cnCodes.map(cnCode => ({
      methodCode: method.code,
      cnCode: cnCode
    }))
  )

  const enumeratedElectricitySources = aggregatedElectricity.sources
    .map(
      (source, index) =>
        `(${repeat('i', index + 1)}) ${t(`common:electricitySources.${source.type}.title`, tLng)}`
    )
    .join(' ')

  const justificationInfoArray = justificationOptions.filter(option =>
    justificationInfo?.includes(option.value)
  )
  const dataQualityInfoArray = dataQualityOptions.filter(option =>
    dataQualityInfo?.includes(option.value)
  )
  const qualityAssuranceInfoArray = qualityAssuranceOptions.filter(option =>
    qualityAssuranceInfo?.includes(option.value)
  )

  const installationDetails = [
    {
      title: t('finalizedReportDocument:installation.address.labels.installationName', tLng),
      value: displayDual(doesRequireLocalAddress, installationName, installation.localName)
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.street', tLng),
      value: displayDual(doesRequireLocalAddress, installation.street, installation.localStreet)
    },
    {
      title: t(
        'finalizedReportDocument:installation.address.labels.additionalAddressInformation',
        tLng
      ),
      value: displayDual(doesRequireLocalAddress, installation.street2, installation.localStreet2)
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.postalCode', tLng),
      value: displayDual(
        doesRequireLocalAddress,
        installation.postCode,
        installation.localPostCode,
        {
          allowAlphaNumeric: true
        }
      )
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.city', tLng),
      value: displayDual(doesRequireLocalAddress, installation.city, installation.localCity)
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.country', tLng),
      value: installationCountryName
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.unlocode', tLng),
      value: unlocodeWithCountry
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.longitude', tLng),
      value: installation.longitude
    },
    {
      title: t('finalizedReportDocument:installation.address.labels.latitude', tLng),
      value: installation.latitude
    }
  ]

  const organizationDetails = [
    {
      title: t('finalizedReportDocument:organization.address.labels.street', tLng),
      value: displayDual(doesRequireLocalAddress, organization.street, organization.localStreet)
    },
    {
      title: t('finalizedReportDocument:organization.address.labels.streetNumber', tLng),
      value: displayDual(
        doesRequireLocalAddress,
        organization.streetNumber,
        organization.localStreetNumber,
        {
          allowAlphaNumeric: true
        }
      )
    },
    {
      title: t(
        'finalizedReportDocument:organization.address.labels.additionalAddressInformation',
        tLng
      ),
      value: displayDual(doesRequireLocalAddress, organization.street2, organization.localStreet2)
    },
    {
      title: t('finalizedReportDocument:organization.address.labels.postalCode', tLng),
      value: displayDual(
        doesRequireLocalAddress,
        organization.postCode,
        organization.localPostCode,
        {
          allowAlphaNumeric: true
        }
      )
    },
    {
      title: t('finalizedReportDocument:organization.address.labels.city', tLng),
      value: displayDual(doesRequireLocalAddress, organization.city, organization.localCity)
    },
    {
      title: t('finalizedReportDocument:organization.address.labels.poBox', tLng),
      value: displayDual(doesRequireLocalAddress, organization.poBox, organization.localPoBox, {
        allowAlphaNumeric: true
      })
    },
    {
      title: t('finalizedReportDocument:organization.address.labels.country', tLng),
      value: organizationCountryName
    }
  ]

  const contactDetails = [
    {
      title: t('finalizedReportDocument:organization.contact.labels.contactPersonName', tLng),
      value: organization.contactName
    },
    {
      title: t('finalizedReportDocument:organization.contact.labels.phoneNumber', tLng),
      value: organization.contactPhoneNumber
    },
    {
      title: t('finalizedReportDocument:organization.contact.labels.emailAddress', tLng),
      value: organization.contactEmail
    }
  ]

  useEffect(() => {
    let isMounted = true
    const requiredNamespaces = [
      'finalizedReportDocument',
      'common',
      'countriesByIsoAlpha2',
      'productionMethods'
    ]

    const ensureLangResources = async () => {
      if (!selectedPdfLanguage) {
        if (isMounted) {setIsLangReady(true)}
        return
      }

      const nsToLoad = requiredNamespaces.filter(
        ns => !i18n.hasResourceBundle(selectedPdfLanguage, ns)
      )

      if (nsToLoad.length === 0) {
        if (isMounted) {setIsLangReady(true)}
        return
      }

      try {
        await i18n.loadLanguages(selectedPdfLanguage)
        await i18n.reloadResources(selectedPdfLanguage, nsToLoad)
      } finally {
        if (isMounted) {setIsLangReady(true)}
      }
    }

    setIsLangReady(false)
    void ensureLangResources()

    return () => {
      isMounted = false
    }
  }, [selectedPdfLanguage, i18n])

  if (!isLangReady) {
    return <ArtemSpinner className="w-10 h-10 mx-auto" />
  }

  // Dynamic pagination calculations
  const hasMultiGoods = cnCodes.length > 1
  const justificationThreshold = 3
  const needsJustificationSplit = justificationInfoArray.length > justificationThreshold
  // Consider qualifying params present if any value-like field exists
  const hasQualifyingParams = Object.values(emissionQualifyingParams || {}).some((qualifyingParam: any) => {
    return (
      qualifyingParam?.value !== undefined && qualifyingParam?.value !== null
    ) || (Array.isArray(qualifyingParam?.elements) && qualifyingParam.elements.length > 0) || (Array.isArray(qualifyingParam?.materials) && qualifyingParam.materials.length > 0)
  })
  // If qualifying params exist and there are more than 1 justifications, split before justifications
  const needsSplitByEqpAndJust = hasQualifyingParams && justificationInfoArray.length > 1
  const needsSplit = needsJustificationSplit || needsSplitByEqpAndJust
  const totalPages = 4 + (hasMultiGoods ? 1 : 0) + (needsSplit ? 1 : 0)

  const introPageNum = 1
  const installPageNum = hasMultiGoods ? 2 : null
  const emissionsPageNum = hasMultiGoods ? 3 : 2
  const supportingPage1Num = hasMultiGoods ? 4 : 3
  const supportingPage2Num = needsSplit ? supportingPage1Num + 1 : null
  const contactPageNum = (supportingPage2Num ?? supportingPage1Num) + 1

  return (
    <div className="max-w-5xl mx-auto text-sm mb-10">
      {/* Cover Page */}
      <FinalizedReportCoverPage
        operatorName={operatorName}
        cnCodes={cnCodes}
        startDate={startDateFormatted}
        endDate={endDateFormatted}
        creationDate={creationDateFormatted}
        finalizedDate={finalizedDateFormatted}
        productionRouteName={productionRoute.name}
        entityConfig={FINALIZED_REPORT_CONFIG.artem}
        selectedPdfLanguage={selectedPdfLanguage}
        isPreview={isPreview}
      />

      {/* Page 1: Intro + Installation & Production Route (conditionally split) */}
      <FinalizedReportPage
        isPreview={isPreview}
        showWatermark={isPreview}
        pageNumber={String(introPageNum)}
        totalPage={String(totalPages)}
        entityConfig={FINALIZED_REPORT_CONFIG.artem}
        selectedPdfLanguage={selectedPdfLanguage}
      >
        <div className="flex-grow space-y-6">
          <section className="space-y-4">
            <SectionTitle>{t('finalizedReportDocument:introduction.title', tLng)}</SectionTitle>
            <p>{t('finalizedReportDocument:introduction.p1', { ...tLng, operatorName })}</p>
            <p>{t('finalizedReportDocument:introduction.p2', tLng)}</p>
            <p>{t('finalizedReportDocument:introduction.p3', tLng)}</p>
            <p>
              {t('finalizedReportDocument:introduction.p4', {
                ...tLng,
                finalizedDate: finalizedDateFormatted
              })}
            </p>
          </section>

          {/* Render Installation & Production Route here only if not multiple goods */}
          {cnCodes.length <= 1 && (
            <section className="space-y-4">
              <header className="space-y-2">
                <SectionTitle>
                  {t('finalizedReportDocument:installationAndRoute.title', tLng)}
                </SectionTitle>
              </header>

              <div className="space-y-2">
                <SubSectionTitle>
                  {t('finalizedReportDocument:installation.address.subtitle', tLng)}
                </SubSectionTitle>
                <p>{t('finalizedReportDocument:installation.address.description', tLng)}</p>
                <FinalizedReportDetailsTable items={installationDetails} />
              </div>

              <div className="space-y-2">
                <SubSectionTitle>
                  {t('finalizedReportDocument:installation.productionRoute.subtitle', tLng)}
                </SubSectionTitle>
                <p className="mb-4">
                  {t('finalizedReportDocument:installation.productionRoute.description', {
                    ...tLng,
                    aggregatedGood: t(
                      `common:aggregatedGoods.${productionRoute.aggregatedGood}`,
                      tLng
                    ),
                    productionMethod,
                    productionRouteName
                  })}
                </p>
                <ul className="list-disc pl-5 mb-4 font-semibold">
                  {cnCodes.map(method => (
                    <li key={method.methodCode}>{method.cnCode}</li>
                  ))}
                </ul>
                <p>
                  {t('finalizedReportDocument:installation.productionRoute.normalisedNote', tLng)}
                </p>
              </div>
            </section>
          )}
        </div>
      </FinalizedReportPage>

      {/* If multiple goods, move Installation & Production Route to a dedicated page */}
      {hasMultiGoods && (
        <FinalizedReportPage
          isPreview={isPreview}
          showWatermark={isPreview}
          pageNumber={String(installPageNum!)}
          totalPage={String(totalPages)}
          entityConfig={FINALIZED_REPORT_CONFIG.artem}
          selectedPdfLanguage={selectedPdfLanguage}
        >
          <div className="flex-grow space-y-4">
            <header className="space-y-2">
              <SectionTitle>
                {t('finalizedReportDocument:installationAndRoute.title', tLng)}
              </SectionTitle>
            </header>

            <div className="space-y-2">
              <SubSectionTitle>
                {t('finalizedReportDocument:installation.address.subtitle', tLng)}
              </SubSectionTitle>
              <p>{t('finalizedReportDocument:installation.address.description', tLng)}</p>
              <FinalizedReportDetailsTable items={installationDetails} />
            </div>

            <div className="space-y-2">
              <SubSectionTitle>
                {t('finalizedReportDocument:installation.productionRoute.subtitle', tLng)}
              </SubSectionTitle>
              <p className="mb-4">
                {t('finalizedReportDocument:installation.productionRoute.description', {
                  ...tLng,
                  aggregatedGood: t(
                    `common:aggregatedGoods.${productionRoute.aggregatedGood}`,
                    tLng
                  ),
                  productionMethod,
                  productionRouteName
                })}
              </p>
              <ul className="list-disc pl-5 mb-4 font-semibold">
                {cnCodes.map(method => (
                  <li key={method.methodCode}>{method.cnCode}</li>
                ))}
              </ul>
              <p>
                {t('finalizedReportDocument:installation.productionRoute.normalisedNote', tLng)}
              </p>
            </div>
          </div>
        </FinalizedReportPage>
      )}

      {/* Page: Emissions (page index shifts if multi-goods, but numbering handled by CSS) */}
      <FinalizedReportPage
        isPreview={isPreview}
        showWatermark={isPreview}
        pageNumber={String(emissionsPageNum)}
        totalPage={String(totalPages)}
        entityConfig={FINALIZED_REPORT_CONFIG.artem}
        selectedPdfLanguage={selectedPdfLanguage}
      >
        <div className="flex-grow space-y-6">
          <SectionTitle>{t('finalizedReportDocument:emissions.title', tLng)}</SectionTitle>

          <section className="space-y-2">
            <SubSectionTitle>
              {t('finalizedReportDocument:emissions.specificEmbedded.title', tLng)}
            </SubSectionTitle>
            <p>{t('finalizedReportDocument:emissions.specificEmbedded.description', tLng)}</p>

            <FinalizedReportSpecificEmbeddedEmissionsTable
              methodAggregatedEmissions={singleMethodAggregatedEmissions}
              selectedPdfLanguage={selectedPdfLanguage}
            />

            <p className="text-xs italic text-text-secondary mb-8">
              {t('finalizedReportDocument:emissions.specificEmbedded.note', tLng)}
            </p>
          </section>

          <section className="space-y-2">
            <SubSectionTitle>
              {t('finalizedReportDocument:emissions.indirect.title', tLng)}
            </SubSectionTitle>
            <p>{t('finalizedReportDocument:emissions.indirect.description', tLng)}</p>
            <FinalizedReportCombinedElectricityEmissionsTable
              methodAggregatedEmissions={singleMethodAggregatedEmissions}
              selectedPdfLanguage={selectedPdfLanguage}
            />
            <p>{t('finalizedReportDocument:emissions.indirect.mixIntro', tLng)}</p>
            <FinalizedReportElectricityEmissionsTable
              aggregatedElectricity={aggregatedElectricity}
              electricityCombined={electricityCombined}
              selectedPdfLanguage={selectedPdfLanguage}
            />

            <div className="space-y-4">
              <p>
                {t(
                  'finalizedReportDocument:emissions.indirect.gridIeafactor',
                  {
                    ...tLng,
                    countryName: installationCountryName
                  }
                )}
              </p>
              {isUsingDirectTechnicalLink && (
                <p>
                  {t('finalizedReportDocument:emissions.indirect.dtlIntro', tLng)}
                  <br />
                  {
                    productionRoute.electricityProductionMethods.find(
                      pm => pm.type === 'DIRECT_TECHNICAL_LINK'
                    )?.info
                  }
                </p>
              )}
              {isUsingPPA && (
                <p>
                  {t('finalizedReportDocument:emissions.indirect.ppaIntro', tLng)}
                  {productionRoute.electricityProductionMethods.find(pm => pm.type === 'PPA')?.info}
                </p>
              )}
              <p>{t('finalizedReportDocument:emissions.indirect.otherSourceIntro', tLng)}</p>
              <div className="space-y-2 bg-gray-100 p-2 italic">
                {hasPrecursorEmissionFactors && (
                  <p>{t('finalizedReportDocument:emissions.indirect.precursorFactors', tLng)}</p>
                )}
                <p>
                  {t('finalizedReportDocument:emissions.indirect.weightedAvgFactor', {
                    ...tLng,
                    sources: enumeratedElectricitySources
                  })}
                  {isUsingGrid && t('finalizedReportDocument:emissions.indirect.gridSuffix', tLng)}
                  {isUsingDirectTechnicalLink &&
                    t('finalizedReportDocument:emissions.indirect.dtlSuffix', tLng)}
                  {isUsingPPA && t('finalizedReportDocument:emissions.indirect.ppaSuffix', tLng)}
                  {t('finalizedReportDocument:emissions.indirect.docNote', tLng)}
                </p>
              </div>
            </div>
          </section>
        </div>
      </FinalizedReportPage>

      {/* Page: Additional supporting documents (split Justifications if long) */}
      {(() => {
        const hasJustifications = justificationInfoArray.length > 0
        const firstCount = needsSplitByEqpAndJust ? 0 : justificationThreshold
        const firstJustifications = justificationInfoArray.slice(0, firstCount)
        const restJustifications = justificationInfoArray.slice(firstCount)

        // Sequential lettering for subsection titles (a, b, c, ...)
        const takeLetter = (() => {
          let idx = 0
          return () => String.fromCharCode(97 + idx++)
        })()
        let justificationLetter: string | null = null
        const getJustificationLetter = () => {
          if (!justificationLetter) {justificationLetter = takeLetter()}
          return justificationLetter
        }

        return (
          <>
            <FinalizedReportPage
              isPreview={isPreview}
              showWatermark={isPreview}
              pageNumber={String(supportingPage1Num)}
              totalPage={String(totalPages)}
              entityConfig={FINALIZED_REPORT_CONFIG.artem}
              selectedPdfLanguage={selectedPdfLanguage}
            >
              <div className="flex-grow space-y-6">
                <SectionTitle>{t('finalizedReportDocument:supportingInfo.title', tLng)}</SectionTitle>
                <div className="space-y-2">
                  <SubSectionTitle>
                    {`${takeLetter()}. ${t('finalizedReportDocument:supportingInfo.qualifying.title', tLng)}`}
                  </SubSectionTitle>
                  <p>{t('finalizedReportDocument:supportingInfo.qualifying.description', tLng)}</p>
                  <FinalizedReportEmissionQualifyingParams
                    emissionQualifyingParams={emissionQualifyingParams}
                    selectedPdfLanguage={selectedPdfLanguage}
                  />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>
                    {`${takeLetter()}. ${t('finalizedReportDocument:supportingInfo.dataQuality.title', tLng)}`}
                  </SubSectionTitle>
                  <p className="mb-4">
                    <span className="font-semibold">
                      {dataQualityInfoArray.map(option => t(option.labelKey, tLng)).join(', ')}
                    </span>{' '}
                    <br />
                    <span>{dataQualityInfoArray.map(option => t(option.infoKey, tLng)).join(', ')}</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>
                    {`${takeLetter()}. ${t('finalizedReportDocument:supportingInfo.dataAssurance.title', tLng)}`}
                  </SubSectionTitle>
                  <p>
                    <span className="font-semibold">
                      {qualityAssuranceInfoArray.map(option => t(option.labelKey, tLng)).join(', ')}
                    </span>{' '}
                    <br />
                    <span>
                      {qualityAssuranceInfoArray.map(option => t(option.infoKey, tLng)).join(', ')}
                    </span>
                  </p>
                </div>
                {hasJustifications && (!needsSplit || firstJustifications.length > 0) && (
                  <div className="space-y-2">
                    <SubSectionTitle>
                      {`${getJustificationLetter()}. ${t('finalizedReportDocument:supportingInfo.justification.title', tLng)} `}
                    </SubSectionTitle>
                    {(!needsSplit ? justificationInfoArray : firstJustifications).map(each => (
                      <div key={each.value}>
                        <h6 className="font-semibold">{t(each.labelKey, tLng)}</h6>
                        <p>{t(each.infoKey, tLng)}</p>
                      </div>
                    ))}
                  </div>
                )}
                {!needsSplit && isApplicableForActivityLevelValidation && (
                  <div className="space-y-2">
                    <SubSectionTitle>
                      {`${takeLetter()}. ${t('finalizedReportDocument:supportingInfo.activityLevelValidation.title', tLng)}`}
                    </SubSectionTitle>
                    <p>
                      {t(
                        'finalizedReportDocument:supportingInfo.activityLevelValidation.description',
                        tLng
                      )}
                    </p>
                  </div>
                )}
              </div>
            </FinalizedReportPage>

            {needsSplit && restJustifications.length > 0 && (
              <FinalizedReportPage
                isPreview={isPreview}
                showWatermark={isPreview}
                pageNumber={String(supportingPage2Num!)}
                totalPage={String(totalPages)}
                entityConfig={FINALIZED_REPORT_CONFIG.artem}
                selectedPdfLanguage={selectedPdfLanguage}
              >
                <div className="flex-grow space-y-6">
                  <SectionTitle>{t('finalizedReportDocument:supportingInfo.title', tLng)}</SectionTitle>
                  <div className="space-y-2">
                    <SubSectionTitle>
                      {`${getJustificationLetter()}. ${t('finalizedReportDocument:supportingInfo.justification.title', tLng)} `}
                    </SubSectionTitle>
                    {restJustifications.map(each => (
                      <div key={each.value}>
                        <h6 className="font-semibold">{t(each.labelKey, tLng)}</h6>
                        <p>{t(each.infoKey, tLng)}</p>
                      </div>
                    ))}
                  </div>
                  {isApplicableForActivityLevelValidation && (
                    <div className="space-y-2">
                      <SubSectionTitle>
                        {`${takeLetter()}. ${t('finalizedReportDocument:supportingInfo.activityLevelValidation.title', tLng)}`}
                      </SubSectionTitle>
                      <p>
                        {t(
                          'finalizedReportDocument:supportingInfo.activityLevelValidation.description',
                          tLng
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </FinalizedReportPage>
            )}
          </>
        )
      })()}

      {/* Page: Contact + About (remains last) */}
      <FinalizedReportPage
        isPreview={isPreview}
        showWatermark={isPreview}
        pageNumber={String(contactPageNum)}
        totalPage={String(totalPages)}
        entityConfig={FINALIZED_REPORT_CONFIG.artem}
        selectedPdfLanguage={selectedPdfLanguage}
      >
        <div className="flex-grow space-y-6">
          <section className="space-y-2">
            <header className="space-y-2">
              <SectionTitle>{t('finalizedReportDocument:contact.title', tLng)}</SectionTitle>
              <p className="mt-2 mb-4">{t('finalizedReportDocument:contact.description', tLng)}</p>
            </header>
            <div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <SubSectionTitle>
                    {t('finalizedReportDocument:contact.organizationName.subtitle', tLng)}
                  </SubSectionTitle>
                  <FinalizedReportDetailsTable
                    items={[
                      {
                        title: t('finalizedReportDocument:contact.organizationName.label', tLng),
                        value: displayDual(
                          doesRequireLocalAddress,
                          organization.name,
                          organization.localName
                        )
                      }
                    ]}
                  />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>
                    {t('finalizedReportDocument:contact.operatorAddress.subtitle', tLng)}
                  </SubSectionTitle>
                  <FinalizedReportDetailsTable items={organizationDetails} />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>
                    {t('finalizedReportDocument:contact.operatorContact.subtitle', tLng)}
                  </SubSectionTitle>
                  <FinalizedReportDetailsTable items={contactDetails} />
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <SectionTitle>{t('finalizedReportDocument:about.title', tLng)}</SectionTitle>
            <p>
              <strong>ARTEM Operators</strong> {t('finalizedReportDocument:about.p1', tLng)}
            </p>
            <p>
              {t('finalizedReportDocument:about.p2.before', tLng)} <strong>ARTEM Declarants</strong>{' '}
              {t('finalizedReportDocument:about.p2.afterBeforeFree', tLng)}{' '}
              <strong>{t('finalizedReportDocument:about.p2.freeOption', tLng)}</strong>!
            </p>
            <p>
              <strong>ARTEM Declarants</strong> {t('finalizedReportDocument:about.p3', tLng)}
            </p>
          </section>
          <FinalizedReportDeclarantsBanner />
        </div>

        {/* do not remove the below line as it is needed for PDF */}
        <div data-artem="render-completed"></div>
      </FinalizedReportPage>
    </div>
  )
}
